import { isNonEmptyArray, toCamelCase } from "../../../common/utils";
import { FIELD_TYPES } from "../constants";

export const normalizeCommitmentData = (commitment) => {
	if (!commitment) {
		return null;
	}
	let normalizedPilot = [];
	if (commitment.pilot) {
		if (Array.isArray(commitment.pilot)) {
			normalizedPilot = commitment.pilot.map((pilot) => {
				if (typeof pilot === "object" && pilot !== null) {
					return {
						id: pilot.id || null,
						displayName: pilot.displayName || pilot.name || "",
						email: pilot.email || "",
					};
				}
				return {
					id: null,
					displayName: String(pilot),
					email: "",
				};
			});
		} else if (typeof commitment.pilot === "object" && commitment.pilot !== null) {
			normalizedPilot = [
				{
					id: commitment.pilot.id || null,
					displayName: commitment.pilot.displayName || commitment.pilot.name || "",
					email: commitment.pilot.email || "",
				},
			];
		} else {
			normalizedPilot = [
				{
					id: null,
					displayName: String(commitment.pilot),
					email: "",
				},
			];
		}
	}
	const normalizeArrayField = (field) => {
		if (!field) {
			return [];
		}
		if (Array.isArray(field)) {
			return field;
		}
		return [field];
	};
	return {
		...commitment,
		pilot: normalizedPilot,
		theme: normalizeArrayField(commitment.theme),
		risk: normalizeArrayField(commitment.risk),
		priority: normalizeArrayField(commitment.priority),
		frequency: normalizeArrayField(commitment.frequency),
	};
};

export const normalizeCommitments = (commitments) => {
	if (!Array.isArray(commitments)) {
		return [];
	}
	return commitments.map(normalizeCommitmentData);
};

export const transformCommitmentForApi = (formData) => {
	const payload = {
		details: formData.details || null,
	};
	if (formData.pilot) {
		if (Array.isArray(formData.pilot)) {
			payload.pilot = formData.pilot.filter((p) => p && p.id).map((p) => p.id);
		} else if (formData.pilot.id) {
			payload.pilot = [formData.pilot.id];
		}
	}
	return payload;
};

export const getPilotDisplayName = (pilot) => {
	if (!pilot) {
		return "";
	}
	if (Array.isArray(pilot)) {
		if (pilot.length === 0) {
			return "";
		}
		if (pilot.length === 1) {
			return pilot[0]?.displayName || pilot[0]?.name || pilot[0] || "";
		}
		return pilot.map((p) => p?.displayName || p?.name || p).join(", ");
	}

	if (typeof pilot === "object") {
		return pilot.displayName || pilot.name || "";
	}

	return String(pilot);
};

export const getPilotEmail = (pilot) => {
	if (!pilot) {
		return "";
	}
	if (Array.isArray(pilot)) {
		return pilot[0]?.email || "";
	}
	if (typeof pilot === "object") {
		return pilot.email || "";
	}
	return "";
};

export const formatDate = (dateValue) => {
	if (!dateValue) {
		return "-";
	}
	const date = typeof dateValue === "string" ? new Date(dateValue) : dateValue;
	if (Number.isNaN(date.getTime())) {
		return "-";
	}
	const day = String(date.getDate()).padStart(2, "0");
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const year = date.getFullYear();
	return `${day}/${month}/${year}`;
};

export const extractValue = (v) => {
	if (typeof v === "object" && v !== null) {
		return v.valueUuid || v.themeUuid || v.valueId || v.value || v;
	}
	return v;
};

export const buildFilterPayload = (filterDefinitions, appliedFilters) => {
	const payload = {};
	const fieldUuidToNameMap = {};
	const fieldTypeMap = {};
	if (isNonEmptyArray(filterDefinitions)) {
		filterDefinitions.forEach((field) => {
			fieldUuidToNameMap[field.fieldUuid] = toCamelCase(field.fieldName);
			fieldTypeMap[field.fieldUuid] = field.type;
		});
	}
	Object.entries(appliedFilters).forEach(([filterKey, values]) => {
		const fieldUuid =
			(filterKey === "documents" &&
				Object.keys(fieldTypeMap).find((key) => fieldTypeMap[key] === FIELD_TYPES.DOCUMENT)) ||
			filterKey;
		if (fieldUuid === "content" || fieldUuid === "separator") {
			return;
		}
		if (!values || (Array.isArray(values) && values.length === 0)) {
			return;
		}
		const fieldName = fieldUuidToNameMap[fieldUuid] || fieldUuid;
		const fieldType = fieldTypeMap[fieldUuid];
		if (fieldType === FIELD_TYPES.DATE && isNonEmptyArray(values)) {
			if (values.length === 1 && values[0].value) {
				payload[toCamelCase(fieldName)] = {
					operator: values[0].relation || "EQ",
					startDate: values[0].value || null,
					endDate: null,
				};
			}
			if (values.length === 2 && values.every((val) => val?.value)) {
				payload[toCamelCase(fieldName)] = {
					operator: "EQ",
					startDate: values[0].value || null,
					endDate: values[1].value || null,
				};
			}
		} else if (fieldType === FIELD_TYPES.CATEGORY && typeof values === "object" && values !== null) {
			if (isNonEmptyArray(values?.elements)) {
				payload[fieldName] = {
					is: values.is !== undefined ? values.is : true,
					matchAll: values.matchAll !== undefined ? values.matchAll : false,
					elements: values.elements.map((element) => element.valueUuid),
				};
			} else if (isNonEmptyArray(values)) {
				const extractedValues = values.map(extractValue);
				if (extractedValues.length > 0) {
					payload[fieldName] = {
						is: true,
						matchAll: false,
						elements: extractedValues,
					};
				}
			}
		} else if (fieldType === FIELD_TYPES.TEXT && typeof values === "string") {
			payload[fieldName] = values;
		} else if (fieldType === FIELD_TYPES.DOCUMENT) {
			payload.documents = values;
		} else if (fieldType === FIELD_TYPES.BOOLEAN) {
			payload[fieldName] = values.value;
		} else if (fieldType === FIELD_TYPES.USER && isNonEmptyArray(values)) {
			payload[fieldName] = values.map(({ value }) => value);
		} else if (isNonEmptyArray(values)) {
			const extractedValues = values.map(extractValue);
			if (extractedValues.length > 0) {
				payload[fieldName] = {
					is: true,
					matchAll: false,
					elements: extractedValues,
				};
			}
		}
	});
	if (appliedFilters.content) {
		payload.details = appliedFilters.content;
		payload.name = appliedFilters.content;
	}
	return payload;
};

export const findDefaultTheme = (items, targetId) => {
	const itemWithId = items.find((item) => item.valueUuid === targetId && item.commitmentCount > 0);
	if (itemWithId) {
		return targetId;
	}
	const firstPositive = items.find((item) => item.commitmentCount > 0);
	return firstPositive?.valueUuid;
};
